import { Loader2 } from "lucide-react"
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from "@/components/ui/entityActionButtonClasses"
import { PageHeader } from "@/features/invitee/components/PageHeader"
import type { TransferProcessPageStateProps } from "../types"

/** Loading / error guards before the wizard shell renders (Angular entity GET gate). */
export function TransferProcessPageState({
  fragment,
  pageHeaderTitle,
  entityId,
  entityLoadStatus,
  navigate,
}: TransferProcessPageStateProps) {
  if (!fragment) {
    return (
      <div
        data-testid="transfer-process-page-invalid-hash"
        className="min-h-screen min-w-0 w-full bg-[#f4f7f6] font-sans text-slate-900"
      >
        <PageHeader title={pageHeaderTitle} testIdRoot="transfer-process-page-header" />
        <div className="w-full px-3 py-6 sm:px-6">
          <p data-testid="transfer-process-invalid-hash-message" className="text-slate-700">
            Invalid or missing transfer type in the URL hash.
          </p>
          <p data-testid="transfer-process-invalid-hash-hint" className="mt-2 text-sm text-slate-500">
            Open this page from Property Edit → Transfer and pick an option, or append e.g.{" "}
            <code className="rounded bg-slate-100 px-1">#transfer_property</code> to the URL.
          </p>
        </div>
      </div>
    )
  }

  if (!entityId.trim()) {
    return (
      <div
        data-testid="transfer-process-page-missing-entity"
        className="min-h-screen min-w-0 w-full bg-[#f4f7f6] font-sans text-slate-900"
      >
        <PageHeader title={pageHeaderTitle} testIdRoot="transfer-process-page-header" />
        <div className="w-full px-3 py-6 sm:px-6">
          <p data-testid="transfer-process-missing-entity-message" className="text-slate-700">
            Missing property or company id in the URL.
          </p>
        </div>
      </div>
    )
  }

  if (entityLoadStatus === "pending") {
    return (
      <div
        data-testid="transfer-process-page-loading"
        className="min-h-screen min-w-0 w-full bg-[#f4f7f6] font-sans text-slate-900"
      >
        <PageHeader title={pageHeaderTitle} testIdRoot="transfer-process-page-header" />
        <div
          data-testid="transfer-process-loading-indicator"
          className="flex justify-center items-center py-40 w-full"
        >
          <Loader2 className="h-10 w-10 animate-spin text-slate-400" aria-hidden />
        </div>
      </div>
    )
  }

  if (entityLoadStatus === "failed") {
    return (
      <div
        data-testid="transfer-process-page-load-failed"
        className="min-h-screen min-w-0 w-full bg-[#f4f7f6] font-sans text-slate-900"
      >
        <PageHeader title={pageHeaderTitle} testIdRoot="transfer-process-page-header" />
        <div className="w-full max-w-lg px-3 py-8 sm:px-6">
          <p data-testid="transfer-process-load-failed-title" className="text-base font-medium text-slate-800">
            {fragment === "transfer_company"
              ? "This company could not be loaded."
              : "This property could not be loaded."}
          </p>
          <p data-testid="transfer-process-load-failed-description" className="mt-2 text-sm text-slate-600">
            It may not exist, may have been removed, or you may not have access. Transfer cannot continue.
          </p>
          <div data-testid="transfer-process-load-failed-actions" className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="transfer-process-load-failed-go-back-button"
              onClick={() => navigate(-1)}
              className={secondaryEntityActionButtonClass}
            >
              Go back
            </button>
            <button
              type="button"
              data-testid="transfer-process-load-failed-list-button"
              onClick={() =>
                navigate(
                  fragment === "transfer_company" ? "/company" : "/property",
                )
              }
              className={primaryEntityActionButtonClass}
            >
              {fragment === "transfer_company" ? "Company list" : "Property list"}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
